import type {
  Product,
  ProductListConfig,
  SocialProofMessage,
  OnlineStockData,
  RenderableProduct,
} from "../types/types";
import fetchSocialProofData, {
  getSocialProofAppIdFromConfig,
  shouldFetchSocialProof,
} from "./fetchSocialProofData";
import { useRef } from "react";
import {
  fetchOnlineStockDataForProducts,
  shouldFetchOnlineStock,
} from "./fetchOnlineStockData";

/**
 * Custom hook to enrich a list of products with social proof and online stock data.
 *
 * Returns products synchronously, each with `socialProofMessages` and
 * `initialVariantOnlineStock` as promises. Consumers should unwrap these with
 * React.use() inside Suspense boundaries.
 *
 * Enrichment requests are batched and cached by product position and identity.
 */
export const useEnrichedProducts = (
  products: RenderableProduct[],
  config: ProductListConfig,
  consumerId: string,
  instanceId?: string,
): RenderableProduct[] => {
  const cacheRef = useRef<Map<string, EnrichmentCacheEntry>>(new Map());

  // Enrichment fetches are client-only. On the server, return products with
  // resolved empty promises so React.use() never suspends during SSR.
  // Skeleton products are returned unaltered.
  if (typeof window === "undefined") {
    return products.map((product) =>
      product.isSkeleton
        ? product
        : {
            ...product,
            socialProofMessages: Promise.resolve(undefined),
            initialVariantOnlineStock: Promise.resolve(undefined),
          },
    );
  }

  // Skeleton products are excluded from enrichment (no caching or fetching).
  const indexed: IndexedProduct[] = products
    .map((product, index) => ({ product, index }))
    .filter(
      (entry): entry is { product: Product; index: number } =>
        entry.product.isSkeleton !== true,
    )
    .map(({ product, index }) => ({
      product,
      key: productKey(product, index),
    }));

  // Evict cache entries for products no longer in the list.
  // This implicitly handles both fresh product lists (all entries evicted)
  // and appended lists (only stale entries evicted).
  const currentKeys = new Set(indexed.map(({ key }) => key));
  cacheRef.current = new Map(
    [...cacheRef.current].filter(([key]) => currentKeys.has(key)),
  );

  // Batch fetch enrichment data for all products not yet in the cache
  const uncached = indexed.filter(({ key }) => !cacheRef.current.has(key));

  if (uncached.length > 0) {
    const productList = uncached.map(({ product }) => product);

    const socialProofBatch = (
      shouldFetchSocialProof(config)
        ? fetchSocialProofData(
            productList,
            getSocialProofAppIdFromConfig(config),
            config?.name,
            consumerId,
            instanceId,
          )
        : Promise.resolve([])
    ).catch(() => []);

    const onlineStockBatch = (
      shouldFetchOnlineStock(config)
        ? fetchOnlineStockDataForProducts(
            productList,
            config.name,
            consumerId,
            instanceId,
          )
        : Promise.resolve([])
    ).catch(() => []);

    const newEntries: [string, EnrichmentCacheEntry][] = uncached.map(
      ({ product, key }) => [
        key,
        {
          socialProofMessages: socialProofBatch.then(
            (data) =>
              data.find((sp) => sp.productId === product.aliases.pimProductId)
                ?.messages,
          ),
          initialVariantOnlineStock: onlineStockBatch.then((data) =>
            data.find((os) =>
              product.variants.map((v) => v.aliases.skuId).includes(os.skuId),
            ),
          ),
        },
      ],
    );

    cacheRef.current = new Map([...cacheRef.current, ...newEntries]);
  }

  // Skeleton products are returned unaltered; enriched products are looked up
  // by their original position/identity key.
  return products.map((product, i) => {
    if (product.isSkeleton) {
      return product;
    }
    const entry = cacheRef.current.get(productKey(product, i))!;
    return {
      ...product,
      socialProofMessages: entry.socialProofMessages,
      initialVariantOnlineStock: entry.initialVariantOnlineStock,
    };
  });
};

type IndexedProduct = { product: Product; key: string };

type EnrichmentCacheEntry = {
  socialProofMessages: Promise<SocialProofMessage[] | undefined>;
  initialVariantOnlineStock: Promise<OnlineStockData | undefined>;
};

/**
 * Builds a key that uniquely identifies a product instance within a list.
 * Index is included because the same product (or even the same product with
 * the same selected SKU) can appear more than once, so product-level IDs
 * alone cannot guarantee uniqueness.
 */
const productKey = (product: Product, index: number): string =>
  `${index}:${product.aliases.pimProductId}`;
